import { FaUsers, FaBuilding, FaBoxes, FaExclamationTriangle } from "react-icons/fa";

const defaultCards = [
  {
    title: "Total Employees",
    value: 0,
    change: "",
    icon: <FaUsers className="text-lg text-blue-600" />,
    bgColor: "bg-blue-50",
    ringColor: "ring-blue-100",
    accentColor: "border-l-blue-500",
  },
  {
    title: "Total Departments",
    value: 0,
    change: "",
    icon: <FaBuilding className="text-lg text-purple-600" />,
    bgColor: "bg-purple-50",
    ringColor: "ring-purple-100",
    accentColor: "border-l-purple-500",
  },
  {
    title: "Total Designations",
    value: 0,
    change: "",
    icon: <FaBoxes className="text-lg text-green-600" />,
    bgColor: "bg-green-50",
    ringColor: "ring-green-100",
    accentColor: "border-l-green-500",
  },
  {
    title: "Total Teams",
    value: 0,
    change: "",
    icon: <FaExclamationTriangle className="text-lg text-red-600" />,
    bgColor: "bg-red-50",
    ringColor: "ring-red-100",
    accentColor: "border-l-red-500",
  },
];

export default function DashboardCards({ data, isStaff = false }) {
  // For staff dashboard, show different cards
  if (isStaff) {
    const approvedLeaves = data?.approved_leave_count || 0;
    const rejectedLeaves = data?.rejected_leave_count || 0;
    const attendance = data?.attendance || {};
    const presentCount = attendance?.present_count || 0;
    const leaveCount = attendance?.leave_count || 0;
    const totalCount = attendance?.total_count || 0;

    const staffCards = [
      {
        title: "Total Monthly Approved Leaves",
        value: approvedLeaves,
        change: "",
        icon: <FaUsers className="text-lg text-green-600" />,
        bgColor: "bg-green-50",
        ringColor: "ring-green-100",
        accentColor: "border-l-green-500",
      },
      {
        title: "Total Monthly Rejected Leaves",
        value: rejectedLeaves,
        change: "",
        icon: <FaExclamationTriangle className="text-lg text-red-600" />,
        bgColor: "bg-red-50",
        ringColor: "ring-red-100",
        accentColor: "border-l-red-500",
      },
      {
        title: "Total Monthly Present Days",
        value: presentCount,
        change: totalCount > 0 ? `of ${totalCount} total` : "",
        icon: <FaBuilding className="text-lg text-blue-600" />,
        bgColor: "bg-blue-50",
        ringColor: "ring-blue-100",
        accentColor: "border-l-blue-500",
      },
      {
        title: "Total Monthly Leave Days",
        value: leaveCount,
        change: "",
        icon: <FaBoxes className="text-lg text-purple-600" />,
        bgColor: "bg-purple-50",
        ringColor: "ring-purple-100",
        accentColor: "border-l-purple-500",
      },
    ];

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {staffCards.map((card, idx) => (
          <div
            key={idx}
            className={`p-5 bg-white rounded-xl border border-borderColor border-l-[3px] ${card.accentColor} hover:shadow-sm transition-shadow`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-gray-500 text-sm mb-2">{card.title}</h4>
                <h2 className="text-2xl font-bold text-gray-900">{card.value}</h2>
                {card.change && (
                  <p className="text-xs text-gray-500 mt-1">{card.change}</p>
                )}
              </div>
              <div className={`w-11 h-11 rounded-full ${card.bgColor} ring-4 ${card.ringColor} flex items-center justify-center`}>
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Map API data to cards based on actual API response structure (Admin dashboard)
  const totalEmployees = data?.staffInformation?.total_staff || data?.additionalInfo?.total_user || 0;
  const totalDepartments = data?.total_departments ? data.total_departments : 0;
  const totalDesignations = data?.total_designations ? data.total_designations : 0;
  const totalTeams = data?.teamInformation?.total_teams || 0;

  const cards = data ? [
    {
      title: "Total Employees",
      value: totalEmployees,
      change: data?.staffInformation?.new_joiners_this_month ? `+${data.staffInformation.new_joiners_this_month} this month` : "",
      icon: <FaUsers className="text-lg text-blue-600" />,
      bgColor: "bg-blue-50",
      ringColor: "ring-blue-100",
      accentColor: "border-l-blue-500",
    },
    {
      title: "Total Departments",
      value: totalDepartments,
      change: "",
      icon: <FaBuilding className="text-lg text-purple-600" />,
      bgColor: "bg-purple-50",
      ringColor: "ring-purple-100",
      accentColor: "border-l-purple-500",
    },
    {
      title: "Total Designations",
      value: totalDesignations,
      change: "",
      icon: <FaBoxes className="text-lg text-green-600" />,
      bgColor: "bg-green-50",
      ringColor: "ring-green-100",
      accentColor: "border-l-green-500",
    },
    {
      title: "Total Teams",
      value: totalTeams,
      change: "",
      icon: <FaExclamationTriangle className="text-lg text-red-600" />,
      bgColor: "bg-red-50",
      ringColor: "ring-red-100",
      accentColor: "border-l-red-500",
    },
  ] : defaultCards;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {cards.map((card, idx) => (
        <div
          key={idx}
          className={`p-5 bg-white rounded-xl border border-borderColor border-l-[3px] ${card.accentColor} hover:shadow-sm transition-shadow`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-gray-500 text-sm mb-2">{card.title}</h4>
              <h2 className="text-2xl font-bold text-gray-900">{card.value}</h2>
              {card.change && (
                <p className={`text-xs mt-1 ${card.change.startsWith('+') ? 'text-green-600' : card.change.startsWith('-') ? 'text-red-600' : 'text-gray-500'}`}>
                  {card.change}
                </p>
              )}
            </div>
            <div className={`w-11 h-11 rounded-full ${card.bgColor} ring-4 ${card.ringColor} flex items-center justify-center`}>
              {card.icon}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
